import { groupApi } from '@/api/services/groupApi';
import { CheckBox, TextBox } from '@/components/ui';
import { createChinaAppSysGroup, type ChinaAppSysGroup } from '@/types/entities';

import { useAdminChanges } from '../adminChanges';
import { AdminNotice } from '../AdminNotice';
import { AdminGroupMembersList } from '../lists/AdminGroupMembersList';
import { AdminGroupViewsList } from '../lists/AdminGroupViewsList';
import { AdminFormFrame } from './AdminFormFrame';
import { useAdminForm } from './useAdminForm';

/** Port von `IwmAdminGruppeForm.razor` in der Gestalt aus REQ-25 §4.6 (REQ-28 §3). */
export interface AdminGroupFormProps {
  /** `0` legt eine neue Gruppe an. */
  id: number;
  onSave: (id: number) => void | Promise<void>;
}

/** Der Wortlaut steht so in REQ-28 §3. */
const ADMIN_HINT =
  'Administrative Gruppen haben alle Rechte auf allen Views. Die Einstellungen unten haben dann keine Wirkung.';

const fetch = (id: number) => groupApi.getEntry(id);
const create = (model: ChinaAppSysGroup) => groupApi.create(model);
const put = (id: number, model: ChinaAppSysGroup) => groupApi.put(id, model);
const remove = (id: number) => groupApi.remove(id);

export function AdminGroupForm(props: AdminGroupFormProps) {
  const changes = useAdminChanges();
  const form = useAdminForm({
    id: props.id,
    createEmpty: createChinaAppSysGroup,
    fetch,
    create,
    put,
    remove,
    deleteMessage:
      'Möchten Sie diese Gruppe wirklich löschen? Dieser Vorgang kann nicht rückgängig gemacht werden.',
    deleteTitle: 'Gruppe löschen',
    // Eine Gruppe entscheidet über Navigation und Rechte — die Navigation zieht nach.
    onSave: async (id) => {
      await props.onSave(id);
      changes.notifyNavigationChanged();
    },
  });
  const { model } = form;
  const existing = model.Id > 0;

  return (
    <AdminFormFrame
      title={existing ? (model.Bezeichnung ?? undefined) : undefined}
      showDelete={existing}
      saving={form.saving}
      isValid={form.isValid}
      dirty={form.dirty}
      onNew={form.onNew}
      onRefresh={() => form.reload()}
      onDelete={form.onDelete}
      onSave={form.onSave}
    >
      <div className="iwm-admin-form-grid">
        <TextBox
          value={model.Bezeichnung}
          onChange={(value) => {
            form.setField('Bezeichnung', value);
          }}
          label="Name"
        />
        <CheckBox
          checked={model.IstAdministrativ}
          onChange={(checked) => {
            form.setField('IstAdministrativ', checked);
          }}
          label="Administrative"
        />
        {model.IstAdministrativ ? <AdminNotice tone="warning">{ADMIN_HINT}</AdminNotice> : null}
      </div>
      {existing ? (
        <>
          <AdminGroupMembersList parentId={model.Id} />
          <AdminGroupViewsList parentId={model.Id} />
        </>
      ) : (
        <AdminNotice>
          Mitglieder und Views lassen sich erst zuordnen, wenn die Gruppe gespeichert ist.
        </AdminNotice>
      )}
    </AdminFormFrame>
  );
}

export default AdminGroupForm;
